/* eslint-disable react/no-unused-prop-types,no-console,max-len */
import React from 'react';
import PropTypes from 'prop-types';
import ReactTable from 'react-table';
import ContentLoader from 'react-content-loader';
import { formatRoute } from 'react-router-named-routes';
import { withTranslation } from 'react-i18next';
import moment from 'moment';
import 'moment/locale/es';
import Gravatar from 'react-gravatar';
// material-ui icons
import Refresh from '@material-ui/icons/Refresh';
import Agency from '@material-ui/icons/SupervisorAccountRounded';
import LinearProgress from '@material-ui/core/LinearProgress';
// material-ui/ core components
import extendedTablesStyle from 'assets/jss/material-dashboard-pro-react/views/extendedTablesStyle';
import withStyles from '@material-ui/core/styles/withStyles';
// core components
import GridItem from 'components/UI/Grid/GridItem';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
// global variables
import { creditBundlesStatus } from 'config';
// our components
import { USER } from 'routes/paths';
import RouterContext from 'helpers/routerContext';
import { LoadingAnimation } from 'components/common/Loading';
import Paginator from 'components/common/Paginator';
import { TableLoader } from 'components/common/TableLoader';
import Datepicker from 'components/common/DatePicker';
import WithPermission from 'components/User/WithPermission';
import { getPaidAdDetailsUrl, getUserPhotoUrl } from 'helpers/functions';

/**
 * @class
 * @extends React.Component
 */
class CreditBundlesReport extends React.Component {
  static contextType = RouterContext;

  /**
   * @description
   * Redirect to ads/details route.
   *
   * @param {string} id - Ad id.
   * @param {string} paidAdType - Type.
   */
  handleDetailsPaidAd = (id, paidAdType) => {
    const history = this.context;
    const url = getPaidAdDetailsUrl(id, paidAdType);
    history.push(url);
  };

  /**
   * @description
   * Redirect to user/details route.
   *
   * @param {string} id - User id.
   */
  handleDetailsUser = id => {
    const history = this.context;
    history.push(formatRoute(USER.DETAILS, { id }));
  };

  /**
   * @description
   * Format frequency and duration received from the server.
   *
   * @param {Object} param - Frequency or duration received from the server.
   * @param {Object} i18n - Translation.
   * @returns {Object} Converted and translated frequency and duration.
   */
  changeFormatToString = (
    { years, months, days, hours, minutes, seconds },
    i18n,
  ) => {
    moment.locale(i18n.language);
    return moment()
      .add({ seconds, minutes, hours, days, months, years })
      .fromNow(true);
  };

  /**
   * @description
   * Render Status cell.
   *
   * @param {Object} node - An ad.
   * @returns {*} Status cell.
   */
  renderCellStatus = node => {
    const { classes, t } = this.props;

    switch (node.status) {
      case creditBundlesStatus.CREATED:
        return (
          <h6 className={`${classes.cellStatus} ${classes.pendingTag}`}>
            {t(`credit_bundles.list.status.${creditBundlesStatus.CREATED}`)}
          </h6>
        );
      case creditBundlesStatus.STARTED:
        return (
          <h6 className={`${classes.cellStatus} ${classes.runningTag}`}>
            {t(`credit_bundles.list.status.${creditBundlesStatus.STARTED}`)}
          </h6>
        );
      case creditBundlesStatus.COMPLETED:
        return (
          <h6 className={`${classes.cellStatus} ${classes.completedTag}`}>
            {t(`credit_bundles.list.status.${creditBundlesStatus.COMPLETED}`)}
          </h6>
        );
      case creditBundlesStatus.EXPIRED:
        return (
          <h6 className={`${classes.cellStatus} ${classes.deletedTag}`}>
            {t(`credit_bundles.list.status.${creditBundlesStatus.EXPIRED}`)}
          </h6>
        );
      default:
        break;
    }
    return null;
  };

  /**
   * @description
   * Description of ReactTable columns.
   *
   * @returns {Array} Table columns.
   */
  renderColumns = () => {
    const { classes, t } = this.props;
    return [
      {
        Header: (
          <span className={classes.headerField}>
            {t('credit_bundles.list.headers.id')}
          </span>
        ),
        accessor: 'id',
        sortable: false,
        filterable: false,
        minWidth: 90,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('credit_bundles.list.headers.owner')}
          </span>
        ),
        accessor: 'owner',
        sortable: false,
        filterable: false,
        minWidth: 120,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('credit_bundles.list.headers.paidads')}
          </span>
        ),
        accessor: 'paidads',
        sortable: false,
        filterable: false,
        minWidth: 90,
      },
      {
        Header: (
          <span className={classes.headerField} style={{ textAlign: 'center' }}>
            <p>{t('credit_bundles.list.headers.status')}</p>
          </span>
        ),
        accessor: 'status',
        sortable: false,
        filterable: false,
        minWidth: 100,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('paidad.report.list.headers.sales')}
          </span>
        ),
        accessor: 'sales',
        sortable: false,
        filterable: false,
        maxWidth: 90,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('paidad.report.list.headers.commission')}
          </span>
        ),
        accessor: 'commission',
        sortable: false,
        filterable: false,
        maxWidth: 110,
      },
      {
        Header: (
          <span className={classes.headerField} style={{ textAlign: 'left' }}>
            <p>{t('paidad.report.list.headers.net_sales')}</p>
          </span>
        ),
        accessor: 'net_sales',
        sortable: false,
        filterable: false,
        maxWidth: 90,
      },
      {
        expander: true,
        show: false,
      },
    ];
  };

  /**
   * @description
   * Map the data from api to ReactTable rows.
   *
   * @param {Array} edges - All ads from api.
   * @param {Function} t - Translation function.
   * @returns {Array} Table rows.
   */
  renderRows = (edges, t) =>
    edges.map(({ node }) => ({
      id: node.id,
      owner:
        node.user && node.user.fullname ? (
          <WithPermission
            alt={
              node.user && (
                <div>
                  <Agency />
                  {node.user.fullname}
                </div>
              )
            }
            allowPermissions={['view_useragency', 'view_ownership_useragency']}
          >
            <div id={`bundle-owner-${node.user.id}-${node.id}`}>
              <Button
                data-cy="paidAdOwner"
                simple
                color="primary"
                style={{ margin: 0, padding: 0 }}
                onClick={() => this.handleDetailsUser(node.user.id)}
              >
                <Agency />
                {node.user.fullname}
              </Button>
            </div>
          </WithPermission>
        ) : (
          <div
            style={{
              marginTop: 10,
              fontSize: 15,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Agency />
            <span>{`${t('promoted_ad.form.fields.private_user')} (${
              node.user.fakeId
            })`}</span>
          </div>
        ),
      paidads: <span data-cy="price">{`${node.paidads.edges.length}/5`}</span>,
      status: <span data-cy="status">{this.renderCellStatus(node)}</span>,
      sales: (
        <div>
          <p data-cy="agencySales" key={`agency-sales-${node.id}`}>
            {`${(node.secondaryPrice ? node.secondaryPrice : node.price) +
              (node.rate ? node.rate * node.commission : node.commission)} ${
              node.secondaryCurrency ? node.secondaryCurrency : node.currency
            }`}
          </p>
        </div>
      ),
      commission: (
        <div>
          <p data-cy="agencySales" key={`agency-commission-${node.id}`}>
            {`${node.rate ? node.rate * node.commission : node.commission} ${
              node.secondaryCurrency ? node.secondaryCurrency : node.currency
            }`}
          </p>
        </div>
      ),
      net_sales: (
        <div>
          <p data-cy="agencySales" key={`agency-netsales-${node.id}`}>
            {`${node.secondaryPrice ? node.secondaryPrice : node.price} ${
              node.secondaryCurrency ? node.secondaryCurrency : node.currency
            }`}
          </p>
        </div>
      ),
    }));

  /**
   * @description
   * Render Filter, Paginator and ReactTable components.
   * @returns {*} Filter, Paginator and ReactTable components.
   */
  render() {
    const {
      data,
      loading,
      fullLoading,
      changeNumberOfRows,
      changePage,
      numberOfRowsSelected,
      paginationDirection,
      classes,
      filter,
      appliedFilter,
      refresh,
      t,
    } = this.props;
    const edges =
      loading || data === undefined
        ? []
        : data.agencySummaries.agency.creditBundles.edges;
    const agency =
      loading || data === undefined ? undefined : data.agencySummaries.agency;
    const rows = this.renderRows(edges, t);
    const cols = this.renderColumns();
    return (
      <React.Fragment>
        <GridContainer>
          <GridItem xs={12}>
            <div className={classes.pageContainer}>
              <GridContainer justify="space-between" alignItems="center">
                <GridItem>
                  <h3 className={classes.title}>
                    {t('paidad.report.details.title')}
                  </h3>
                </GridItem>
                <GridItem>
                  <Button
                    color="primary"
                    simple
                    className={classes.showFilters}
                    onClick={() => refresh()}
                    id="refresh"
                    key="refresh"
                  >
                    <Refresh />
                    {t('ad.filters.update')}
                  </Button>
                </GridItem>
              </GridContainer>
              <GridContainer>
                <GridItem xs={12}>
                  <div className={classes.contentContainer}>
                    <div
                      className={[
                        classes.loadingLineContainer,
                        classes.topLine,
                      ].join(' ')}
                    >
                      {' '}
                      {fullLoading && (
                        <LinearProgress
                          classes={{
                            barColorPrimary: classes.loadingLine,
                          }}
                        />
                      )}
                    </div>
                    <GridContainer>
                      <GridItem xs={12} sm={8} md={8}>
                        <GridContainer>
                          <GridItem xs={12} sm={8} md={4}>
                            <Datepicker
                              data-cy="paidAdsReportFilterStartDateFrom"
                              title={t(
                                // eslint-disable-next-line max-len
                                'paidad.report.list.filters.date.from_placeholder',
                              )}
                              defaultValue={t(
                                // eslint-disable-next-line max-len
                                'paidad.report.list.filters.date.from_placeholder',
                              )}
                              required
                              onChange={date => {
                                filter({
                                  startDateGte: moment(date)
                                    .startOf('day')
                                    .format(),
                                });
                              }}
                              value={
                                appliedFilter.startDateGte !== undefined
                                  ? moment(appliedFilter.startDateGte)
                                  : ''
                              }
                              validGte={appliedFilter.startDateLte}
                            />
                          </GridItem>
                          <GridItem xs={12} sm={8} md={4}>
                            <Datepicker
                              data-cy="paidAdsReportFilterStartDateTo"
                              title={t(
                                // eslint-disable-next-line max-len
                                'paidad.report.list.filters.date.to_placeholder',
                              )}
                              defaultValue={t(
                                // eslint-disable-next-line max-len
                                'paidad.report.list.filters.date.to_placeholder',
                              )}
                              required
                              onChange={date => {
                                filter({
                                  startDateLte: moment(date)
                                    .endOf('day')
                                    .format(),
                                });
                              }}
                              value={
                                appliedFilter.startDateLte !== undefined
                                  ? moment(appliedFilter.startDateLte)
                                  : ''
                              }
                              validLte={appliedFilter.startDateGte}
                            />
                          </GridItem>
                        </GridContainer>
                      </GridItem>
                      <GridItem xs={12} sm={4} md={4}>
                        {!fullLoading ? (
                          <Paginator
                            pageInfo={
                              data.agencySummaries.agency.creditBundles.pageInfo
                            }
                            changePage={changePage}
                            changeNumberOfRows={changeNumberOfRows}
                            paginationDirection={paginationDirection}
                            numberOfRowsSelected={numberOfRowsSelected}
                          />
                        ) : (
                          <ContentLoader height={67} width={327} speed={2}>
                            <path
                              // eslint-disable-next-line max-len
                              d="M199,16 L304,16 C307.313708,16 310,18.6862915 310,22 L310,46 C310,49.3137085 307.313708,52 304,52 L199,52 C195.686292,52 193,49.3137085 193,46 L193,22 C193,18.6862915 195.686292,16 199,16 Z M25.9861352,27.0121317 L70.0138648,27.0121317 C74.4244855,27.0121317 78,30.5876462 78,34.9982669 C78,39.4088876 74.4244855,42.9844021 70.0138648,42.9844021 L25.9861352,42.9844021 C21.5755145,42.9844021 18,39.4088876 18,34.9982669 C18,30.5876462 21.5755145,27.0121317 25.9861352,27.0121317 Z M103.986135,27.0121317 L148.013865,27.0121317 C152.424485,27.0121317 156,30.5876462 156,34.9982669 C156,39.4088876 152.424485,42.9844021 148.013865,42.9844021 L103.986135,42.9844021 C99.5755145,42.9844021 96,39.4088876 96,34.9982669 C96,30.5876462 99.5755145,27.0121317 103.986135,27.0121317 Z"
                              fill="#D8D8D8"
                            />
                          </ContentLoader>
                        )}
                      </GridItem>
                    </GridContainer>
                    <GridContainer style={{ padding: '15' }}>
                      <GridItem xs={12}>
                        {agency && (
                          <GridContainer>
                            <GridItem>
                              <div className={classes.userDetails}>
                                <h6>
                                  {t('paidad.report.list.headers.agency')}
                                </h6>
                                <WithPermission
                                  alt={
                                    <React.Fragment>
                                      <span
                                        key="agency-avatar"
                                        className={classes.avatarContainer}
                                      >
                                        {agency.photo ? (
                                          <img
                                            src={
                                              agency.fullname === 'Revolico'
                                                ? 'assets/img/Logo.svg'
                                                : getUserPhotoUrl(agency.photo)
                                            }
                                            alt="Avatar"
                                          />
                                        ) : (
                                          <Gravatar
                                            email={
                                              agency.email || agency.fullname
                                            }
                                          />
                                        )}
                                      </span>
                                      <span
                                        style={{ marginLeft: '8px' }}
                                        key="agency-fullname"
                                      >
                                        {agency.fullname}
                                      </span>
                                    </React.Fragment>
                                  }
                                  allowPermissions={['view_agency']}
                                >
                                  <a
                                    data-cy="agencyReportDetails"
                                    id={`agency-name-${agency.id}`}
                                    className={[
                                      'userDetail',
                                      classes.btnAgency,
                                    ].join(' ')}
                                    href={formatRoute(USER.DETAILS, {
                                      id: agency.id,
                                    })}
                                    onClick={e => {
                                      e.preventDefault();
                                      this.handleDetailsUser(agency.id);
                                    }}
                                  >
                                    <span
                                      key="agency-avatar"
                                      className={classes.avatarContainer}
                                    >
                                      {agency.photo ? (
                                        <img
                                          src={
                                            agency.fullname === 'Revolico'
                                              ? 'assets/img/Logo.svg'
                                              : getUserPhotoUrl(agency.photo)
                                          }
                                          alt="Avatar"
                                        />
                                      ) : (
                                        <Gravatar
                                          email={
                                            agency.email || agency.fullname
                                          }
                                        />
                                      )}
                                    </span>
                                    <span
                                      style={{ marginLeft: '8px' }}
                                      key="agency-fullname"
                                    >
                                      {agency.fullname}
                                    </span>
                                  </a>
                                </WithPermission>
                              </div>
                            </GridItem>
                            <GridItem>
                              <div className={classes.userDetails}>
                                <h6>{t('paidad.report.list.headers.count')}</h6>
                                <p className={classes.colAgencyDetails}>
                                  {
                                    data.agencySummaries.creditBundleSummaries.reduce(
                                      (acc, current) => ({
                                        count: current.count + acc.count,
                                      }),
                                      { count: 0 },
                                    ).count
                                  }
                                </p>
                              </div>
                            </GridItem>
                            <GridItem>
                              <div className={classes.userDetails}>
                                <h6>{t('paidad.report.list.headers.sales')}</h6>
                                {data.agencySummaries.creditBundleSummaries.map(
                                  summary => (
                                    <p
                                      className={classes.colAgencyDetails}
                                      data-cy="agencySales"
                                      key={`agency-sales-${summary.currency}-${agency.id}`}
                                    >
                                      {`${summary.grossSales} ${summary.currency}`}
                                    </p>
                                  ),
                                )}
                              </div>
                            </GridItem>
                            <GridItem>
                              <div className={classes.userDetails}>
                                <h6>
                                  {t('paidad.report.list.headers.commission')}
                                </h6>
                                {data.agencySummaries.creditBundleSummaries.map(
                                  summary => (
                                    <p
                                      className={classes.colAgencyDetails}
                                      data-cy="agencyCommissions"
                                      key={`agency-commissions-${summary.currency}-${agency.id}`}
                                    >
                                      {`${summary.commissions} ${summary.currency}`}
                                    </p>
                                  ),
                                )}
                              </div>
                            </GridItem>
                            <GridItem>
                              <div className={classes.userDetails}>
                                <h6>
                                  {t('paidad.report.list.headers.net_sales')}
                                </h6>
                                {data.agencySummaries.creditBundleSummaries.map(
                                  summary => (
                                    <p
                                      className={classes.colAgencyDetails}
                                      data-cy="agencyNetSales"
                                      key={`agency-netsales-${summary.currency}-${agency.id}`}
                                    >
                                      {`${summary.netSales} ${summary.currency}`}
                                    </p>
                                  ),
                                )}
                              </div>
                            </GridItem>
                          </GridContainer>
                        )}
                      </GridItem>
                    </GridContainer>
                    <GridContainer style={{ marginTop: 20 }}>
                      <GridItem xs={12}>
                        <div>
                          {loading ? (
                            <TableLoader />
                          ) : (
                            <ReactTable
                              data={rows}
                              filterable
                              columns={cols}
                              loading={loading}
                              pageSize={
                                edges.length < numberOfRowsSelected
                                  ? edges.length
                                  : numberOfRowsSelected
                              }
                              showPagination={false}
                              className={[
                                '-striped -highlight',
                                classes.tbl,
                              ].join(' ')}
                              defaultFilterMethod={(field, row) => {
                                const id = field.pivotId || field.id;
                                return row[id] !== undefined
                                  ? String(row[id]).startsWith(field.value)
                                  : true;
                              }}
                              SubComponent={() => (
                                <div className={classes.loading}>
                                  <LoadingAnimation />
                                </div>
                              )}
                              getTrProps={(state, rowInfo) => ({
                                style: {
                                  backgroundColor: rowInfo && '',
                                  borderLeft: rowInfo && '',
                                },
                              })}
                              noDataText={t('credit_bundles.list.no_data')}
                              minRows={8}
                            />
                          )}
                        </div>
                      </GridItem>
                    </GridContainer>
                    <GridContainer justify="flex-end">
                      <GridItem xs={12} sm={4}>
                        {!fullLoading ? (
                          <Paginator
                            pageInfo={
                              data.agencySummaries.agency.creditBundles.pageInfo
                            }
                            changePage={changePage}
                            changeNumberOfRows={changeNumberOfRows}
                            paginationDirection={paginationDirection}
                            numberOfRowsSelected={numberOfRowsSelected}
                          />
                        ) : (
                          <ContentLoader height={67} width={327} speed={2}>
                            <path
                              // eslint-disable-next-line max-len
                              d="M199,16 L304,16 C307.313708,16 310,18.6862915 310,22 L310,46 C310,49.3137085 307.313708,52 304,52 L199,52 C195.686292,52 193,49.3137085 193,46 L193,22 C193,18.6862915 195.686292,16 199,16 Z M25.9861352,27.0121317 L70.0138648,27.0121317 C74.4244855,27.0121317 78,30.5876462 78,34.9982669 C78,39.4088876 74.4244855,42.9844021 70.0138648,42.9844021 L25.9861352,42.9844021 C21.5755145,42.9844021 18,39.4088876 18,34.9982669 C18,30.5876462 21.5755145,27.0121317 25.9861352,27.0121317 Z M103.986135,27.0121317 L148.013865,27.0121317 C152.424485,27.0121317 156,30.5876462 156,34.9982669 C156,39.4088876 152.424485,42.9844021 148.013865,42.9844021 L103.986135,42.9844021 C99.5755145,42.9844021 96,39.4088876 96,34.9982669 C96,30.5876462 99.5755145,27.0121317 103.986135,27.0121317 Z"
                              id="Combined-Shape"
                              fill="#D8D8D8"
                            />
                          </ContentLoader>
                        )}
                      </GridItem>
                    </GridContainer>
                  </div>
                </GridItem>
              </GridContainer>
            </div>
          </GridItem>
        </GridContainer>
      </React.Fragment>
    );
  }
}

/**
 * CreditBundlesReport PropTypes
 * @param classes - prop styles received from withStyles
 * @param data - prop ads query's results
 * @param numberOfRowsSelected - prop current number of rows selected
 * @param appliedFilter - prop pass to Filter
 * @param loading - prop ads query or mutations status
 * @param t - prop from HOC with string translations
 * @param i18n - prop from HOC with string translations
 * @callback changePage - prop pass to Paginator
 * @callback changeNumberOfRows - prop pass to Paginator
 * @callback filter - prop pass to Filter
 * @callback refresh - callback to refresh page
 */

CreditBundlesReport.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  data: PropTypes.shape({}).isRequired,
  numberOfRowsSelected: PropTypes.number.isRequired,
  // eslint-disable-next-line react/require-default-props
  paginationDirection: PropTypes.string,
  changePage: PropTypes.func.isRequired,
  changeNumberOfRows: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  fullLoading: PropTypes.bool.isRequired,
  appliedFilter: PropTypes.shape({}).isRequired,
  filter: PropTypes.func.isRequired,
  refresh: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  i18n: PropTypes.instanceOf(Object).isRequired,
};

// to fix: The withStyles HOC set contextTypes to a children component
// and it have problem with contextType assigned to PermissionList component
// This is a small hack, with intermediary Component to resolved it temporally
const PaidAdReportWrapper = props => <CreditBundlesReport {...props} />;

export default withTranslation()(
  withStyles(extendedTablesStyle)(PaidAdReportWrapper),
);
